import { IAddress, IAddressCoordinate, IAppointmentDate, IDateTime } from './'
import { UUID } from './data-types'
import { ILLMShipmentParseReasoning } from './shipment-exception-types'

export enum StopStatusEnum {
  Arrived = 'Arrived',
  Departed = 'Departed',
  EnRoute = 'En route',
  Pending = 'Pending'
}

/**
 * Type options for a shipment stop
 */
export enum StopTypeEnum {
  Both = 'Both',
  Drop = 'Drop',
  Pick = 'Pick'
}

export type TStopWarehouseGeofenceType = 'polygon' | 'radius'

export interface IStopWarehouseGeofence {
  /**
   * The geofence property of the warehouse (polygon coordinates)
   */
  property?: IAddressCoordinate[]
  /**
   * The geofence radius in miles (used when type is 'radius')
   */
  radius?: number
  /**
   * The type of geofence shape: 'polygon' uses property, 'radius' uses radius
   */
  type?: TStopWarehouseGeofenceType
}

/**
 * The standard Chaine format for a stop's warehouse
 */
export interface IStopWarehouse {
  /**
   * The stop's warehouse address
   */
  address: IAddress

  /**
   * The category of the warehouse location
   */
  category?: string[]

  geofence?: IStopWarehouseGeofence
  /**
   * The location ID of the warehouse
   */
  locationID?: string
  /**
   * The name of the warehouse
   */
  name?: string
  /**
   * The Google map place ID of the warehouse
   */
  placeID?: string
  /**
   * The rating of the warehouse (e.g. 5 (5 start rating))
   */
  rating?: number
  /**
   * The raw address of the warehouse that was originally provided by the TMS
   */
  rawAddress?: IAddress
  /**
   * The reviews of the warehouse (e.g. 129 reviews)
   */
  reviews?: number
  /**
   * The thumbnail of the warehouse
   */
  thumbnail?: string
  /**
   * The timezone of the warehouse
   */
  timezone?: string
  /**
   * Active warehouse hours
   */
  warehouseHours?: string
}

/**
 * The standard Chaine format for a stop
 */
export interface IStop {
  /**
   * The appointment date for the stop
   */
  appointment?: IAppointmentDate
  /**
   * The driver's check in time for a stop
   */
  arrivalDateTime?: TStopStatusDateTime
  /**
   * The driver's check-out time from a stop
   */
  departureDateTime?: TStopStatusDateTime
  /**
   * The driver's ETA to a stop
   */
  eta?: TStopStatusDateTime
  /**
   * The external stop ID from the customer's source TMS when it exists
   */
  externalStopID?: string
  /**
   * UUID of the stop
   */
  id: UUID
  /**
   * The instructions for the stop
   */
  instructions?: string
  /**
   * Indicates if the stop is the destination
   */
  isDestination: boolean
  /**
   * Whether this stop is a drop-trailer stop when the source TMS provides that signal.
   */
  isDropTrailer?: boolean | null
  /**
   * Indicates if the stop is the origin
   */
  isOrigin: boolean
  /**
   * Pickup or delivery number required for the stop when the driver checks in at the warehouse
   */
  pickupDeliveryNumber?: string
  /**
   * The stop's reference number
   */
  referenceNumber?: string
  /**
   * The sequence of the stop (e.g. 3 (the third stop))
   */
  sequence: number
  /**
   * The stop type, pick or drop
   */
  type: StopTypeEnum
  /**
   * The stop's warehouse info
   */
  warehouse?: IStopWarehouse
}

export interface IPublicStop {
  appointment: IAppointmentDate | null
  id: string
  type: string | null
  warehouse: {
    address: {
      city: string | null
      stateCode: string | null
    }
  } | null
}

export type TStopStatusDateTime = IDateTime & {
  source?: StopSourceEnum
}

export interface IShipmentOriginOrDestination {
  appointment?: IAppointmentDate
  arrivalDateTime?: TStopStatusDateTime
  departureDateTime?: TStopStatusDateTime
  eta?: TStopStatusDateTime
  id: UUID
  warehouse: {
    address: {
      city?: string
      coordinates?: IAddressCoordinate
      stateCode?: string
    }
    name?: string
  }
}

/**
 * The enum to indicate the source from which the stop event was generated
 */
export enum StopSourceEnum {
  AIGenerated = 'AI Generated',
  ChaineWebApp = 'Chaine Web App',
  DriverApp = 'Driver App',
  ExternalIntegration = 'External Integration',
  Geofence = 'Geofence',
  TMSApp = 'TMS App'
}

export interface ISourceMetadata {
  llmReasoning?: ILLMShipmentParseReasoning
}
